import './components/css/app.scss';

import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Routes, Route } from 'react-router-dom';

import {
  DirectRoute,
  PublicRoute
} from './routes';
import { useStore } from './store/storeHooks';
import { ErrorBoundary } from './components/errorBoundary/errorBoundary';

import { PrivateRoute } from './routes/privateRoute';
import { WidgetRoute } from './routes/widgetRoute';
import { OnboardRoute } from './routes/onboardRoute';
import { AuthRoute } from './routes/authRoute';
import { getRouteLookup } from './routes/routeLookup';
import { RouteDescriptor, RouteType } from './routes/routeSchema';
import { Lazy } from './routes/lazy';
import { HistoryRouter } from './routes/historyRouter';
import { AgencyOperatorAccessChunk } from './services/agencyOperatorAccess';

const RouteWrapper = ({ entry, children }: { entry: RouteDescriptor; children: React.ReactNode }) => {
  switch (entry.routeType) {
    case RouteType.Auth:
      return <AuthRoute>{children}</AuthRoute>;
    case RouteType.Direct:
      return <DirectRoute>{children}</DirectRoute>;
    case RouteType.Widget:
      return <WidgetRoute>{children}</WidgetRoute>;
    case RouteType.Private:
      return <PrivateRoute>{children}</PrivateRoute>;
    case RouteType.Public:
      return <PublicRoute>{children}</PublicRoute>;
    case RouteType.Onboard:
      return <OnboardRoute>{children}</OnboardRoute>;
    default:
      return <>{children}</>;
  }
};

export const renderRouteElement = (path: string, entry: RouteDescriptor) => {
  const elem = entry.element;

  const element = (
    <RouteWrapper entry={entry}>
      <Lazy>
        {typeof elem === 'function' ? elem() : elem}
      </Lazy>
    </RouteWrapper>
  );

  // In React Router v7, paths are exact by default
  // Use "*" suffix for non-exact matching
  const routePath = path === '*' ? '*' : (entry.exact === false ? `${path}/*` : path);

  return (
    <Route
      key={path}
      path={routePath}
      element={element}
    />
  );
};

export const App = observer(() => {
  const store = useStore();
  const routeLookup = useMemo(() => getRouteLookup(store), [store]);

  return (
    <div id="app"
      {...store.ui.containerJsxProps}>

      <HistoryRouter history={store.history}>
        <ErrorBoundary>
          <AgencyOperatorAccessChunk />
        </ErrorBoundary>
        <ErrorBoundary>
          <Routes>
            {Object.keys(routeLookup).map(path => {
              const entry = routeLookup[path];
              return renderRouteElement(path, entry);
            })}
          </Routes>
        </ErrorBoundary>
      </HistoryRouter>
    </div>
  );
});
